import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import api from "api";
import { useAuth } from "providers/auth";
import { Features } from "providers/auth/types";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { Enrollment, IssueViews, Views } from "./types";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useURLQuery } from "helpers/useURLQuery";
import { logger } from "logger";

const EnrollmentContext = createContext<{
  enrollment: Enrollment | undefined;
  customerDetails?: {
    ssn?: string;
    dob?: string;
  };
  customerDetailsMeta?: Omit<
    UseQueryResult<
      {
        ssn?: string;
        dob?: string;
      },
      Error
    >,
    "data"
  >;
  enrollmentMeta?: Omit<UseQueryResult<Enrollment | undefined, Error>, "data">;
  canCreateEnrollment: boolean;
  flows: Views[];
  view: Views;
  setView: (view: Views) => void;
  setNextView: () => void;
  setPrevView: () => void;

  patchEnrollmentCase?: UseMutationResult<
    AxiosResponse<unknown, any>[],
    Error,
    Partial<Enrollment>,
    unknown
  >;
  patchCustomerDetails?: UseMutationResult<
    unknown,
    Error,
    {
      ssn: string;
      dob: string;
    },
    unknown
  >;
}>({
  enrollment: undefined,
  flows: [],
  canCreateEnrollment: false,
  view: Views.INTRO,
  setView: () => undefined,
  setNextView: () => undefined,
  setPrevView: () => undefined
});

const flows = {
  // AKA SUPER
  fintech: [
    Views.INTRO,
    // Plaid bypass or plaid token or legacy plaid inside
    Views.PLAID,
    Views.CONFIRM_PERSONAL_INFO,
    Views.CONFIRM_LEASE_INFO,
    Views.PROPERTY_MANAGER_DETAILS,
    Views.CHECK_OUT
  ],
  // PMS
  pms: [
    Views.INTRO,
    Views.CONFIRM_PERSONAL_INFO,
    Views.CONFIRM_LEASE_INFO,
    Views.CHECK_OUT
  ]
};

const issueScreenViews = [IssueViews.LEASE_UPLOAD];

export function EnrollmentProvider({ children }: { children: ReactNode }) {
  const { partner, sdkConfig } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [view, _setView] = useState<Views>(Views.INTRO);
  const queryClient = useQueryClient();

  const params = useParams();
  const query = useURLQuery();

  const { data: dashboard } = useQuery({
    queryKey: ["DASHBOARD"],
    queryFn: () =>
      api.getDashboard().then((res) => {
        return res.data;
      }),
    enabled: Boolean(
      partner?.features?.includes(Features.ENABLE_SDK_RENT_REPORTING)
    )
  });

  const flow = useMemo(() => {
    // TODO: likely to change when we have more partners
    // PMS will never trigger the plaid flow
    if (!partner?.features.includes(Features.SDK)) return flows.pms;

    const excludedViews = [] as string[];

    if (dashboard?.enrollments?.some((enrollment) => enrollment.submitted))
      excludedViews.push(Views.CONFIRM_PERSONAL_INFO);

    if (partner.features.includes(Features.BYPASS_BANK_LINKING))
      excludedViews.push(Views.PLAID);

    return flows.fintech.filter((view) => !excludedViews.includes(view));
  }, [dashboard, partner]);

  const setView = (view: Views) => {
    if (flow.includes(view)) _setView(view);
    else logger.warn(`Invalid view: ${view}. Partner ${partner?.name}`);
  };
  const setNextView = () => {
    const currentIndex = flow.indexOf(view);
    if (currentIndex < flow.length - 1) {
      const currentView = flow[currentIndex + 1];
      _setView(currentView);
      navigate(`/rent_reporting/onboarding/${currentView}`);
    } else navigate("/hub");
  };
  const setPrevView = () => {
    const currentIndex = flow.indexOf(view);
    if (currentIndex > 0) {
      const currentView = flow[currentIndex - 1];
      if (
        sdkConfig.bypassPlaidAccountConfirmation &&
        currentView === Views.PLAID
      ) {
        _setView(Views.INTRO);
        navigate(`/rent_reporting/onboarding/${Views.INTRO}`);
        return;
      } else {
        _setView(currentView);
        navigate(`/rent_reporting/onboarding/${currentView}`);
      }
    } else [navigate("/hub")];
  };

  const { mutate: createEnrollmentCase } = useMutation({
    mutationKey: ["LATEST_ENROLLMENT"],
    mutationFn: () => api.createEnrollmentCase()
  });

  const { data: enrollment, ...enrollmentMeta } = useQuery({
    queryKey: ["LATEST_ENROLLMENT", view],
    queryFn: () =>
      api.getEnrollmentCases().then((res) => {
        if (!res.data.items?.length) createEnrollmentCase();

        return res.data.items?.at(-1);
      })
  });

  const { data: customerDetails, ...customerDetailsMeta } = useQuery({
    queryKey: ["CUSTOMER_DETAILS"],
    queryFn: () => api.getCustomerDetails().then((res) => res.data)
  });

  const patchEnrollmentCase = useMutation({
    mutationKey: ["LATEST_ENROLLMENT", view],
    mutationFn: (data: Partial<Enrollment>) => {
      const promises = [];
      if (
        Object.keys(data).filter((v) =>
          ["address", "legal_entity"].includes(v)
        ) &&
        enrollment?.id
      )
        promises.push(api.updateEnrollmentCase({ ...data, id: enrollment.id }));

      if (data.address && enrollment?.id)
        promises.push(
          api.setEnrollmentCaseAddress({
            id: enrollment.id,
            address: data.address
          })
        );

      if (data.legal_entity && enrollment?.id)
        promises.push(
          api.setEnrollmentCasePayee({
            id: enrollment.id,
            ...data.legal_entity
          })
        );

      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["LATEST_ENROLLMENT", view]
      });
      setNextView();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });
  const patchCustomerDetails = useMutation({
    mutationKey: ["CUSTOMER_DETAILS"],
    mutationFn: (data: { ssn: string; dob: string }) =>
      api.verifyCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["CUSTOMER_DETAILS"]
      });
      setNextView();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  useEffect(() => {
    const paramView = params.view as Views;
    if (params && params.view && flow.includes(paramView)) setView(paramView);
  }, [params.view]);

  useEffect(() => {
    if (
      enrollment?.submitted &&
      !state?.upsell &&
      !query.get("upsell") &&
      !issueScreenViews.includes((params.view as IssueViews) || "") &&
      !dashboard?.can_create_enrollment
    )
      navigate("/hub");
  }, [enrollment?.submitted, state, dashboard?.can_create_enrollment]);

  return (
    <EnrollmentContext.Provider
      value={{
        enrollment,
        enrollmentMeta,
        customerDetails,
        customerDetailsMeta,
        flows: flow,
        view,
        setView: setView,
        setNextView,
        setPrevView,
        canCreateEnrollment: Boolean(dashboard?.can_create_enrollment),

        patchEnrollmentCase,
        patchCustomerDetails
      }}
    >
      {children}
    </EnrollmentContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- okay
export const useEnrollment = () => useContext(EnrollmentContext);
